import { IfAbility, SessionService } from '@efdigital/ll-addon-auth';
import CalendarItemsList from '@efdigital/ll-addon-content/components/calendar/calendar-items-list';
import formatDate, {
  DateFormat,
} from '@efdigital/ll-addon-content/helpers/format-date';
import {
  type CalendarItem,
  DataTypeXceed,
  type Message,
} from '@efdigital/ll-addon-data';
import {
  EditContext,
  EditingStoppedReason,
} from '@efdigital/ll-admin-v2/components/edit-context.gts';
import CalendarTable from '@efdigital/ll-admin-v2/components/programs/calendar/calendar-table.gts';
import { EditCalendarItem } from '@efdigital/ll-admin-v2/components/programs/calendar/edit-calendar-item.gts';
import { EditMessage } from '@efdigital/ll-admin-v2/components/programs/messages/edit-message.gts';
import { ResultsTable } from '@efdigital/ll-admin-v2/components/results-table.gts';
import { setAsyncBelongsTo } from '@efdigital/ll-admin-v2/data/async-setter';
import { queryCalendarItems } from '@efdigital/ll-admin-v2/data/builders/xceed/calendar-items';
import { queryCohortMessages } from '@efdigital/ll-admin-v2/data/builders/xceed/messages';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type Router from '@efdigital/ll-admin-v2/router';
import type ProgramsProgramCohortRoute from '@efdigital/ll-admin-v2/routes/programs/program/cohort';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';
import { pageTitle } from 'ember-page-title';

interface ProgramsProgramCohortScheduleSignature {
  Args: {
    model: ModelFrom<ProgramsProgramCohortRoute>;
    controller: unknown;
  };
}

class ProgramsProgramCohortScheduleTemplate extends Component<ProgramsProgramCohortScheduleSignature> {
  @tracked eventModalOpen = false;
  @tracked announcementModalOpen = false;
  @tracked newEvent: CalendarItem | null = null;
  @tracked newAnnouncement: Message | null = null;
  @service declare store: Store;
  @service declare router: Router;
  @service declare session: SessionService;

  showCreateEvent = () => {
    this.newEvent = this.store.createRecord<CalendarItem>(
      DataTypeXceed.CalendarItem,
      {
        markdown: '',
        location: '',
        external: '',
        category1: 'Xceed',
        category2: 'Cohort',
        category3: this.args.model.program_id,
        category4: this.args.model.cohort_id,
      }
    );
    this.eventModalOpen = true;
  };

  hideCreateEvent = () => {
    this.eventModalOpen = false;
    this.newEvent = null;
  };

  redirectToNewCalendarItem = (reason: EditingStoppedReason) => {
    if (reason === EditingStoppedReason.Saved) {
      const id = this.newEvent!.id;
      this.router.transitionTo(
        'programs.program.cohort.schedule.calendar-item',
        this.args.model.program_id,
        this.args.model.cohort_id,
        id
      );
    } else {
      this.hideCreateEvent();
    }
  };

  showCreateAnnouncement = () => {
    const announcement = this.store.createRecord<Message>(
      DataTypeXceed.Message,
      {
        user: this.session.cached.userId!,
        markdown: '',
        category: 'announcement',
      }
    );
    setAsyncBelongsTo(announcement, 'cohort', this.args.model.cohort);
    this.newAnnouncement = announcement;

    this.announcementModalOpen = true;
  };

  hideCreateAnnouncement = () => {
    this.announcementModalOpen = false;
    this.newAnnouncement = null;
  };

  redirectToNewAnnouncement = (reason: EditingStoppedReason) => {
    if (reason === EditingStoppedReason.Saved) {
      const id = this.newAnnouncement!.id;
      this.router.transitionTo(
        'programs.program.cohort.schedule.announcement',
        this.args.model.program_id,
        this.args.model.cohort_id,
        id
      );
    } else {
      this.hideCreateAnnouncement();
    }
  };

  <template>
    <div class="container py-5">
      {{pageTitle "Schedule"}}

      <div class="row g-5">
        <div class="col-xl-6">
          <IfAbility @ability="calendar" @atLeast="viewer">
            <:if>
              <div>
                <h3 class="gud-eyebrow">Events</h3>

                <p class="small">Events can be displayed on pages by adding an
                  events widget to the page. They can also be attached to
                  announcements which are sent to participants.
                </p>
              </div>

              <IfAbility @ability="calendar" @atLeast="editor">
                <:if>
                  <button
                    type="button"
                    class="btn btn-primary btn-md text-nowrap mb-4"
                    {{on "click" this.showCreateEvent}}
                  >New Event</button>

                  <BsModal
                    @open={{this.eventModalOpen}}
                    @onHidden={{this.hideCreateEvent}}
                    @backdropClose={{false}}
                    @renderInPlace={{true}}
                    @size="xl"
                    as |modal|
                  >
                    <modal.header>
                      <h3 class="modal-title h4">New Event</h3>
                    </modal.header>
                    <modal.body>
                      {{#if this.newEvent}}
                        <EditContext
                          @onEditingStopped={{this.redirectToNewCalendarItem}}
                          as |context|
                        >
                          <div class="row">
                            <div class="col-md-6 py-3">
                              <EditCalendarItem
                                @item={{this.newEvent}}
                                @context={{context}}
                                @showContentFields={{true}}
                                @showEventFields={{true}}
                              />
                            </div>
                            <div class="col-md-6">
                              <div class="sticky-top py-3">
                                <h3 class="gud-eyebrow">Preview</h3>
                                <CalendarItemsList
                                  @calendarItems={{array this.newEvent}}
                                />
                              </div>
                            </div>
                          </div>
                        </EditContext>
                      {{/if}}
                    </modal.body>
                  </BsModal>
                </:if>
              </IfAbility>

              <Request
                @query={{queryCalendarItems @model.program_id @model.cohort_id}}
              >
                <:content as |calendarItems|>
                  <div class="mb-4">
                    <CalendarTable @calendarItems={{calendarItems.data}}>
                      <:title as |calendarItem|>
                        <LinkTo
                          @route="programs.program.cohort.schedule.calendar-item"
                          @models={{array
                            @model.program_id
                            @model.cohort_id
                            calendarItem.id
                          }}
                        >{{calendarItem.title}}</LinkTo>
                      </:title>
                    </CalendarTable>
                  </div>
                </:content>
                <:error as |error|>
                  <p>Error: {{error.message}}</p>
                </:error>
              </Request>
            </:if>
            <:else>
              <div class="alert alert-warning" role="alert">
                <h4 class="alert-heading">Access Denied</h4>
                <p>You do not have permission to view events.</p>
                <p class="mb-0">Please contact your administrator if you believe
                  you should have access.</p>
              </div>
            </:else>
          </IfAbility>
        </div>

        <div class="col-xl-6">
          <IfAbility @ability="messages" @atLeast="viewer">
            <:if>
              <div>
                <h3 class="gud-eyebrow">Announcements</h3>

                <p class="small">Announcements are displayed in the cohort
                  portal and can be sent to participants. They can be displayed
                  immediately or scheduled for a future date.
                </p>
              </div>

              <IfAbility @ability="messages" @atLeast="editor">
                <:if>
                  <button
                    type="button"
                    class="btn btn-primary btn-md text-nowrap mb-4"
                    {{on "click" this.showCreateAnnouncement}}
                  >New Announcement</button>
                  <BsModal
                    @open={{this.announcementModalOpen}}
                    @onHidden={{this.hideCreateAnnouncement}}
                    @backdropClose={{false}}
                    @renderInPlace={{false}}
                    @size="xl"
                    as |modal|
                  >
                    <modal.header>
                      <h3 class="modal-title h4">New Announcement</h3>
                    </modal.header>
                    <modal.body>
                      {{#if this.newAnnouncement}}
                        <EditContext
                          @onEditingStopped={{this.redirectToNewAnnouncement}}
                          as |context|
                        >
                          <div class="row">
                            <EditMessage
                              @item={{this.newAnnouncement}}
                              @programId={{@model.program_id}}
                              @cohortId={{@model.cohort_id}}
                              @showContent={{true}}
                              @showCalendarItems={{true}}
                              @context={{context}}
                            />
                          </div>
                        </EditContext>
                      {{/if}}
                    </modal.body>
                  </BsModal>
                </:if>
              </IfAbility>

              <Request
                @query={{queryCohortMessages @model.cohort_id "announcement"}}
              >
                <:content as |content|>
                  <ResultsTable @data={{content.data}}>
                    <:header>
                      <th scope="col">Title</th>
                      <th scope="col">Date</th>
                    </:header>
                    <:row as |message|>
                      <tr>
                        <th scope="row"><LinkTo
                            @route="programs.program.cohort.schedule.announcement"
                            @models={{array
                              @model.program_id
                              @model.cohort_id
                              message.id
                            }}
                          >{{message.title}}</LinkTo>
                        </th>
                        <td>{{formatDate
                            message.startDate
                            DateFormat.ShortDate
                          }}</td>
                      </tr>
                    </:row>
                    <:empty>
                      <p>No announcements have been created yet.</p>
                    </:empty>
                  </ResultsTable>
                </:content>
                <:error>
                  <div class="alert alert-danger">
                    <iconify-icon inline icon="ph:warning" />
                    Failed to load messages.
                  </div>
                </:error>
              </Request>
            </:if>
          </IfAbility>
        </div>
      </div>
    </div>
    {{outlet}}
  </template>
}

export default ProgramsProgramCohortScheduleTemplate;
